<script lang="ts">
  import {
    PLANNING_START_BOUND_MESSAGE,
    type AppointmentPlanningStartBound,
  } from "../../../../_shared/_logic/start-bound"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { onMount, untrack } from "svelte"
  import LoadingPlaceholder from "@kontextwerk/control-web-frame/components/ui/LoadingPlaceholder.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiAlertCircleOutline, mdiRefresh } from "@mdi/js"
  import {
    fetchCompleteCalendarBoardProjection,
    type CalendarBoardExternalBusyInterval,
    type CalendarBoardPropertyRef,
    type CalendarBoardWorkOrderRef,
  } from "../../../../../../../../../api/kehrblatt/schedule-events"
  import {
    type AppointmentPlanningPreviewRow,
    type KehrblattPlanningSettings,
  } from "../../../../../../../../../api/kehrblatt/planning"
  import { SchedulingCalendar } from "../../../../../../../calendar-api"
  import { buildDayColumns, formatPropertyAddress } from "../../../../../../../calendar-api"
  import { buildCalendarRouteTransitionsFromEvents } from "../../../../../../_logic/route-transitions"
  import {
    addDays,
    buildTimeSlots,
    formatDayHeader,
  } from "../../../../../../../../../_shared/_logic/date-math"
  import { resolveCalendarHourBounds } from "../../../../../../../calendar-api"
  import { scheduleStatusLabels } from "../../../../../../../../../_shared/_logic/status-definitions"
  import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"
  import type {
    BoardActions,
    BoardColumn as BoardColumnType,
    BoardInteractionConfig,
    BoardLayoutConfig,
    EmployeeOption,
  } from "../../../../../../../calendar-api"
  import type {
    AppointmentPlanningCalendarAppointmentChange,
    AppointmentPlanningCalendarSource,
  } from "../../../../_shared/_logic/calendar-contract"
  import type { AppointmentPlanningDateRangeFilter } from "../../../../../../_logic/appointments-contract"
  import type { PlanningPreviewRow } from "../../../../_shared/_logic/table-contract"
  import {
    buildAppointmentPlanningCalendarPreviewModel,
    mergeAppointmentPlanningCalendarProperties,
  } from "./_logic/calendarPreviewModel"
  import {
    buildCalendarAppointmentChangeFromBoardUpdate,
    buildFilteredCombinedAppointmentPlanningEvents,
    clientRowIdFromPreviewEventId,
  } from "./_logic/appointmentMove"
  import {
    buildAppointmentPlanningCalendarVisibleColumnIds,
    buildAppointmentPlanningCalendarWindow,
    type AppointmentPlanningCalendarWindow,
  } from "./_logic/calendarFilters"

  type Props = {
    startBound: AppointmentPlanningStartBound
    organizationId: string
    rows: AppointmentPlanningPreviewRow[]
    planningRows: PlanningPreviewRow[]
    sources: AppointmentPlanningCalendarSource[]
    search: string
    employeeId: string
    dateRange: AppointmentPlanningDateRangeFilter
    employeeOptions: Array<{ label: string; value: string }>
    employeeLabelById: Record<string, string>
    workingHoursByUser?: WorkingHoursByUser
    overview?: boolean
    loadingPreview?: boolean
    allowAppointmentResize?: boolean
    readOnlyRowIds?: string[]
    unavailableReason?: string | null
    onAppointmentChange?: (detail: AppointmentPlanningCalendarAppointmentChange) => void
  }

  const {
    startBound,
    organizationId,
    rows = [],
    planningRows,
    sources = [],
    search,
    employeeId,
    dateRange,
    employeeOptions = [],
    employeeLabelById = {},
    workingHoursByUser = {},
    overview = true,
    loadingPreview = false,
    allowAppointmentResize = false,
    readOnlyRowIds = [],
    unavailableReason = null,
    onAppointmentChange = undefined,
  }: Props = $props()

  const DEFAULT_DAY_START_HOUR = 6
  const DEFAULT_DAY_END_HOUR = 22
  const EVENT_QUERY_LIMIT = 5000
  const SLOT_MINUTES = 5
  const HOUR_HEIGHT = 202.8
  const slotHeight = HOUR_HEIGHT / (60 / SLOT_MINUTES)
  const minEventHeight = 18
  const columnWidth = 360
  const columnGap = 0
  const eventTypeColors: Record<ScheduleEventType, string> = {
    APPOINTMENT: "var(--kontext-accent)",
    ABSENCE: "var(--primary-200)",
    INTERNAL_BLOCK: "var(--text-300)",
  }
  const planningFlagLabels: Record<ScheduleEventPlanningFlag, string> = {
    OK: "",
    TIGHT: "Eng",
    OVERFLOW: "Überlauf",
  }
  const employeeColorPalette = [
    "var(--kontext-accent)",
    "#0ea5e9",
    "#f59e0b",
    "#10b981",
    "#ec4899",
    "#a78bfa",
    "#f97316",
  ]

  let boardEvents = $state<ScheduleEvent[]>([])
  let externalBusyIntervals = $state<CalendarBoardExternalBusyInterval[]>([])
  let boardWorkOrders = $state<CalendarBoardWorkOrderRef[]>([])
  let boardProperties = $state<CalendarBoardPropertyRef[]>([])
  let boardContactOrders = $state<ContactOrder[]>([])
  let loading = $state(false)
  let error = $state<string | null>(null)
  let lastQueryKey: string | null = null
  let requestId = 0
  let activeAbortController: AbortController | null = null
  let loadedCalendarWindow = $state<AppointmentPlanningCalendarWindow | null>(null)
  let now = $state(new Date())
  let flexSlotMinutes = $state<number | null>(60)
  let flexSlotsPerDay = $state<number | null>(3)

  const resolveErrorMessage = (err: unknown) => {
    const message = err instanceof Error ? err.message?.trim() : ""

    return message || "Kalenderdaten konnten nicht geladen werden."
  }

  const dedupeById = <T extends { id?: string | null }>(items: T[]): T[] => {
    const result = new Map<string, T>()
    for (const item of items) {
      const id = String(item?.id ?? "").trim()

      if (id && !result.has(id)) result.set(id, item)
    }

    return Array.from(result.values())
  }

  const applyPlanningSettings = (settings?: KehrblattPlanningSettings | null) => {
    const nextFlexSlotMinutes = Number(settings?.flex_slot_minutes ?? 60)
    flexSlotMinutes = Number.isFinite(nextFlexSlotMinutes)
      ? Math.ceil(Math.max(1, nextFlexSlotMinutes) / SLOT_MINUTES) * SLOT_MINUTES
      : 60
    const nextFlexSlotsPerDay = Number(settings?.flex_slots_per_day ?? 0)
    flexSlotsPerDay = Number.isFinite(nextFlexSlotsPerDay)
      ? Math.max(0, Math.floor(nextFlexSlotsPerDay))
      : 0
  }

  const previewModel = $derived(
    buildAppointmentPlanningCalendarPreviewModel({
      organizationId,
      rows,
      sources,
      employeeLabelById,
      now,
    }),
  )
  const plannedEmployeeIds = $derived.by(() => {
    const planned = new Set(previewModel.employeeIds.map(String).filter(Boolean))
    const ordered = employeeOptions
      .map((option) => String(option.value))
      .filter((id) => planned.has(id))
    const missing = Array.from(planned).filter((id) => !ordered.includes(id))

    return [...ordered, ...missing]
  })
  const calendarWindow = $derived(
    buildAppointmentPlanningCalendarWindow({
      previewEmployeeIds: plannedEmployeeIds,
      dateRange,
      employeeId,
    }),
  )
  const displayCalendarWindow = $derived(loadedCalendarWindow ?? calendarWindow)
  const targetEmployeeIds = $derived(displayCalendarWindow?.employeeIds ?? [])
  const activeRowIds = $derived(
    new Set(rows.map((row) => String(row.client_row_id)).filter(Boolean)),
  )
  const visibleColumnIds = $derived(
    buildAppointmentPlanningCalendarVisibleColumnIds({
      planningRows,
      activeRowIds,
      window: displayCalendarWindow,
      search,
    }),
  )
  const employees = $derived<EmployeeOption[]>(
    targetEmployeeIds.map((employeeId) => {
      const label =
        employeeOptions.find((option) => String(option.value) === employeeId)?.label ||
        previewModel.employeeLabelById[employeeId] ||
        "Mitarbeiter"
      const first = label.split(/\s+/)[0] ?? label

      return { id: employeeId, label, shortLabel: first }
    }),
  )
  const employeeColors = $derived(
    Object.fromEntries(
      employees.map((emp, index) => [
        emp.id,
        employeeColorPalette[index % employeeColorPalette.length],
      ]),
    ),
  )
  const queryKey = $derived(
    !unavailableReason && calendarWindow && calendarWindow.employeeIds.length
      ? `${organizationId}:${calendarWindow.rangeStart.toISOString()}:${calendarWindow.rangeEnd.toISOString()}:${calendarWindow.employeeIds
          .slice()
          .sort()
          .join(",")}`
      : "",
  )

  const clearCalendarData = () => {
    boardEvents = []
    externalBusyIntervals = []
    boardWorkOrders = []
    boardProperties = []
    boardContactOrders = []
    loadedCalendarWindow = null
  }

  const isAbortError = (err: unknown): boolean =>
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")

  const loadCalendarWindow = async (
    key: string,
    window: AppointmentPlanningCalendarWindow,
    options: { force?: boolean; signal?: AbortSignal } = {},
  ) => {
    if (!key || (!options.force && key === lastQueryKey)) return

    lastQueryKey = key

    if (!organizationId || !window.employeeIds.length) {
      clearCalendarData()

      return
    }

    const currentRequestId = ++requestId
    loading = true
    error = null
    try {
      const projection = await fetchCompleteCalendarBoardProjection(
        {
          organizationId,
          employeeIds: window.employeeIds,
          startAtFrom: window.rangeStart.toISOString(),
          startAtTo: window.rangeEnd.toISOString(),
          limit: EVENT_QUERY_LIMIT,
        },
        { signal: options.signal, pageSize: EVENT_QUERY_LIMIT },
      )

      if (currentRequestId !== requestId || key !== lastQueryKey) return

      boardEvents = projection.events ?? []
      externalBusyIntervals = projection.external_busy_intervals ?? []
      boardWorkOrders = projection.work_orders ?? []
      boardProperties = projection.properties ?? []
      boardContactOrders = projection.contact_orders ?? []
      applyPlanningSettings(projection.planning_settings ?? null)
      loadedCalendarWindow = window
    } catch (err) {
      if (currentRequestId !== requestId) return

      if (isAbortError(err)) return

      console.error("appointment_planning_calendar_preview_load_failed", err)
      error = resolveErrorMessage(err)
    } finally {
      if (currentRequestId === requestId) {
        loading = false
      }
    }
  }

  const startCalendarWindowLoad = (
    key: string,
    window: AppointmentPlanningCalendarWindow,
    force = false,
  ) => {
    activeAbortController?.abort()
    const controller = new AbortController()
    activeAbortController = controller
    void loadCalendarWindow(key, window, { force, signal: controller.signal })
  }

  $effect(() => {
    const key = queryKey
    const window = untrack(() => calendarWindow)

    if (!key || !window) {
      requestId += 1
      activeAbortController?.abort()
      activeAbortController = null
      lastQueryKey = null
      clearCalendarData()

      return
    }

    startCalendarWindowLoad(key, window)

    return () => {
      requestId += 1
      activeAbortController?.abort()
    }
  })

  onMount(() => {
    now = new Date()
  })

  const readOnlyRowIdSet = $derived(new Set(readOnlyRowIds.map(String).filter(Boolean)))
  const combinedEvents = $derived(
    buildFilteredCombinedAppointmentPlanningEvents({
      boardEvents,
      model: previewModel,
      context: displayCalendarWindow,
    }),
  )
  const combinedWorkOrders = $derived(
    dedupeById([...boardWorkOrders, ...previewModel.previewWorkOrders]),
  )
  const authoritativePreviewPropertyIds = $derived(
    new Set(
      sources
        .filter((source) => source.routeCoordinatesAuthoritative)
        .map((source) => String(source.property?.id ?? "").trim())
        .filter(Boolean),
    ),
  )
  const combinedProperties = $derived(
    mergeAppointmentPlanningCalendarProperties({
      boardProperties,
      previewProperties: previewModel.previewProperties,
      authoritativePreviewPropertyIds,
    }),
  )
  const dimmedEventIds = $derived.by(() => {
    const result = new Set(boardEvents.map((event) => event.id).filter(Boolean))
    for (const event of combinedEvents) {
      const clientRowId = clientRowIdFromPreviewEventId(event.id)

      if (clientRowId && readOnlyRowIdSet.has(clientRowId)) result.add(event.id)
    }

    return result
  })
  const eventsByEmployee = $derived(
    combinedEvents.reduce(
      (acc, event) => {
        const key = String(event.employee_id ?? "__unassigned__")

        if (!acc[key]) acc[key] = []

        acc[key].push(event)

        return acc
      },
      {} as Record<string, ScheduleEvent[]>,
    ),
  )
  const externalBusyIntervalsByEmployee = $derived(
    externalBusyIntervals.reduce(
      (acc, interval) => {
        if (!acc[interval.employee_id]) acc[interval.employee_id] = []

        acc[interval.employee_id].push(interval)

        return acc
      },
      {} as Record<string, CalendarBoardExternalBusyInterval[]>,
    ),
  )
  const workOrderById = $derived(
    Object.fromEntries(combinedWorkOrders.map((order) => [order.id, order])),
  )
  const propertyById = $derived(
    Object.fromEntries(combinedProperties.map((property) => [property.id, property])),
  )
  const combinedRouteTransitions = $derived(
    buildCalendarRouteTransitionsFromEvents({
      events: combinedEvents,
      workOrderById,
      propertyById,
    }),
  )
  const contactOrderByEventId = $derived.by(() => {
    const eventsByContactOrderId = new Map<string, string>()
    for (const event of combinedEvents) {
      if (event.contact_order_id) eventsByContactOrderId.set(event.contact_order_id, event.id)
    }
    const result: Record<string, ContactOrder> = {}
    for (const order of boardContactOrders) {
      const targetEventId =
        order.schedule_event_id || (order.id ? eventsByContactOrderId.get(order.id) : null)

      if (targetEventId && !result[targetEventId]) result[targetEventId] = order
    }

    return result
  })
  const calendarHourBounds = $derived(
    resolveCalendarHourBounds({
      targetEmployeeIds,
      workingHoursByUser,
      filteredEvents: combinedEvents,
      externalBusyIntervals,
      fallbackDayStartHour: DEFAULT_DAY_START_HOUR,
      fallbackDayEndHour: DEFAULT_DAY_END_HOUR,
    }),
  )
  const slotMinutes = SLOT_MINUTES
  const dayStartHour = $derived(calendarHourBounds.dayStartHour)
  const dayEndHour = $derived(calendarHourBounds.dayEndHour)
  const timeSlots = $derived(buildTimeSlots(dayStartHour, dayEndHour, slotMinutes))
  const timeColumnHeight = $derived(Math.max(0, (timeSlots.length - 1) * slotHeight))
  const layout: BoardLayoutConfig = $derived({
    slotHeight,
    slotMinutes,
    dayStartHour,
    minEventHeight,
    flexSlotMinutes: flexSlotMinutes ?? 60,
    flexSlotsPerDay: flexSlotsPerDay ?? 0,
  })
  const columns = $derived.by<BoardColumnType[]>(() => {
    const projectedColumns = buildDayColumns({
      timezone: startBound.timezone,
      targetEmployeeIds,
      visibleDays: displayCalendarWindow?.visibleDays ?? [],
      eventsByEmployee,
      externalBusyIntervalsByEmployee,
      routeTransitions: combinedRouteTransitions,
      employees,
      calendarSyncEnabledEmployeeIds: new Set(),
      employeeColors,
      workOrderById,
      propertyById,
      contactOrderByEventId,
      dimmedEventIds,
      workingHoursByUser,
      now,
      dayStartHour,
      dayEndHour,
      slotMinutes,
      slotHeight,
      minEventHeight,
      eventTypeColors,
      planningFlagLabels,
      statusLabels: scheduleStatusLabels,
    })

    return visibleColumnIds
      ? projectedColumns.filter((column) => visibleColumnIds.has(column.id))
      : projectedColumns
  })

  const calendarPreviewActions: BoardActions = {
    addHardSlot: () => {},
    addSoftSlot: () => {},
    addBlockerSlot: () => {},
    openEvent: () => {},
    updateEvent: (payload) => {
      const change = buildCalendarAppointmentChangeFromBoardUpdate({
        startBound,
        organizationId,
        payload,
        rows,
        sources,
        employeeLabelById,
        now,
        boardEvents,
        boardWorkOrders,
        boardProperties,
        context: displayCalendarWindow,
      })

      if (change) onAppointmentChange?.(change)
      else if (clientRowIdFromPreviewEventId(payload.event.id)) {
        newNotification({ class: "error", html: PLANNING_START_BOUND_MESSAGE })
      }

      return Boolean(change)
    },
    startCancelReplanDay: () => {},
  }
  const canChangePreviewAppointments = $derived(Boolean(onAppointmentChange) && !loading && !error)
  const canChangeEvent = (eventId: string): boolean => {
    const clientRowId = clientRowIdFromPreviewEventId(eventId)

    return Boolean(clientRowId && !readOnlyRowIdSet.has(clientRowId))
  }
  const interaction: BoardInteractionConfig = $derived({
    canDirectlyEditEvents: canChangePreviewAppointments,
    readOnly: !canChangePreviewAppointments,
    canMoveEvent: (item) => canChangeEvent(item.event.id),
    canResizeEvent: (item) => allowAppointmentResize && canChangeEvent(item.event.id),
  })
</script>

{#if loadingPreview}
  <div class="calendar-preview-state">
    <div class="calendar-preview-loader">
      <LoadingPlaceholder />
      <span>Kalenderansicht wird vorbereitet...</span>
    </div>
  </div>
{:else if !previewModel.previewEvents.length}
  <div class="calendar-preview-empty">
    <p>Für die aktuelle Vorschau wurden keine planbaren Termine gefunden.</p>
  </div>
{:else if unavailableReason}
  <div class="calendar-preview-error" role="alert">
    <Icon path={mdiAlertCircleOutline} size="1.1rem" />
    <span>{unavailableReason}</span>
  </div>
{:else if !calendarWindow}
  <div class="calendar-preview-empty">
    <p>Bitte wählen Sie einen vollständigen Kalenderzeitraum aus.</p>
  </div>
{:else if error}
  <div class="calendar-preview-error" role="alert">
    <Icon path={mdiAlertCircleOutline} size="1.1rem" />
    <span>{error}</span>
    <CTA
      type="tertiaryButton"
      content="Erneut laden"
      small={true}
      darkMode={false}
      onClick={() => {
        if (!queryKey || !calendarWindow) return

        startCalendarWindowLoad(queryKey, calendarWindow, true)
      }}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiRefresh} />
      {/snippet}
    </CTA>
  </div>
{:else if visibleColumnIds && visibleColumnIds.size === 0}
  <div class="calendar-preview-empty">
    <p>Für die aktuelle Suche wurden keine passenden Kalenderspalten gefunden.</p>
  </div>
{:else}
  <div
    class="calendar-preview-shell"
    aria-label="Kalendervorschau"
    style="--cal-header-text:#1f2937;--cal-header-muted:#587089;--cal-readonly-header-text:#1f2937;--cal-readonly-header-muted:#587089;"
  >
    <SchedulingCalendar
      presentation={{ view: overview ? "overview" : "timeline", gridMinutes: 10 }}
      {organizationId}
      timezone={startBound.timezone}
      visibleDays={displayCalendarWindow?.visibleDays ?? []}
      {columns}
      {timeColumnHeight}
      {timeSlots}
      {layout}
      actions={calendarPreviewActions}
      {interaction}
      {now}
      {formatDayHeader}
      formatAddress={formatPropertyAddress}
      {columnGap}
      {columnWidth}
      readOnly={!canChangePreviewAppointments}
      showEventTooltips={true}
      showAppointmentTagTooltip={false}
      softSlotDisplayMode="interactive"
      allowSlotCreation={false}
      showUpdateSuccessNotification={false}
      stickyHeaderTop="var(--calendar-preview-sticky-header-top, 0px)"
    />
    {#if loading}
      <div class="calendar-preview-loading">
        <div class="calendar-preview-loader">
          <LoadingPlaceholder />
          <span>Fixe Termine werden geladen...</span>
        </div>
      </div>
    {/if}
  </div>
{/if}

<style lang="less">
  .calendar-preview-shell {
    --calendar-preview-sticky-header-top: 0px;

    position: relative;
    min-height: 22rem;
  }

  .calendar-preview-shell :global(.column-header .employee-name) {
    color: #1f2937 !important;
    opacity: 1 !important;
    text-shadow: none !important;
  }

  .calendar-preview-shell :global(.column-header .day-label) {
    color: #587089 !important;
    opacity: 1 !important;
    text-shadow: none !important;
  }

  .calendar-preview-state,
  .calendar-preview-empty,
  .calendar-preview-error {
    min-height: 12rem;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0.75rem;
    padding: 2.4rem 1rem 1rem;
    box-sizing: border-box;
    color: var(--text-invers-100);
    background: #f7f7f7;
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 16%, transparent);
  }

  .calendar-preview-empty p {
    margin: 0;
    font-weight: 700;
  }

  .calendar-preview-loader {
    position: sticky;
    top: 2.4rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-invers-100);
    font-weight: 700;
  }

  .calendar-preview-error {
    color: var(--error-200, #b91c1c);
    font-weight: 700;
  }

  .calendar-preview-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 2.4rem 1rem 1rem;
    box-sizing: border-box;
    background: color-mix(in srgb, #f7f7f7 72%, transparent);
    pointer-events: auto;
  }
</style>
